import { Box } from '@mui/material';
import { generatePath, useNavigate } from 'react-router-dom';
import { useGetSignedInUser } from '../../../hooks/useGetSignedInUser';
import { APP_URLS } from '../../../routes/urls';
import {
  closeQualificationDrawer,
  showBackButton,
  updateUsedCriteriaFrom,
} from '../../../store/features/qualification/qualificationSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getEligibleModuleUrl } from '../../../utils/qualification';
import { QualificationInputUpsertFormContainer } from '../shared/QualificationInputUpsertFormContainer';
import { StudentQualificationSelectOption } from '../shared/StudentQualificationSelectOption';
import { QualificationWithAddressInputs } from '../types';

export const StudentQualificationSelectContainer: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isQualificationFormVisible, selectedModule } = useAppSelector((state) => ({
    isQualificationFormVisible: state.qualification.isBackButtonVisible,
    selectedModule: state.qualification.selectedModule,
  }));

  const { currentData: userData } = useGetSignedInUser();
  if (!userData?.user) return null;

  const hasEducation = userData.user.educations?.length !== 0;
  const hasQualification = Boolean(userData.user.qualification);
  const hasBothEducationAndQualification = hasQualification && hasEducation;
  const showQualificationForm = (): void => {
    dispatch(showBackButton());
  };

  const gotoEducationEditPage = (): void => {
    dispatch(closeQualificationDrawer());
    navigate(generatePath(APP_URLS.editEducationInfo, { studentSlug: userData.user?.slug }));
  };

  const useEducationDetailForEligibility = (): void => {
    const url = getEligibleModuleUrl(selectedModule);
    dispatch(closeQualificationDrawer());
    navigate(url);
    dispatch(updateUsedCriteriaFrom('education'));
  };

  const useQualificationForEligibility = (): void => {
    const url = getEligibleModuleUrl(selectedModule);
    dispatch(closeQualificationDrawer());
    navigate(url);
    dispatch(updateUsedCriteriaFrom('qualification'));
  };

  return isQualificationFormVisible ? (
    <Box sx={{ px: 2.5 }}>
      <QualificationInputUpsertFormContainer
        mode={hasQualification ? 'EDIT' : 'CREATE'}
        email={userData.user.email}
        defaultValues={
          {
            ...userData.user.qualification?.qualification,
            address:
              userData.user.qualification?.address_coordinates_unified.location ||
              userData.user.temporary_address_coordinates_unified?.location,
            address_lat:
              userData.user.qualification?.address_coordinates_unified.lat ||
              userData.user.temporary_address_coordinates_unified?.lat,
            address_lng:
              userData.user.qualification?.address_coordinates_unified.lng ||
              userData.user.temporary_address_coordinates_unified?.lng,
          } as unknown as QualificationWithAddressInputs
        }
      />
    </Box>
  ) : (
    <Box>
      {hasBothEducationAndQualification ? (
        <StudentQualificationSelectOption
          PrimaryButtonLabel="Use my education details"
          PrimaryButtonProps={{ onClick: useEducationDetailForEligibility }}
          SecondaryButtonLabel="Use my eligibility qualification"
          SecondaryButtonProps={{ onClick: useQualificationForEligibility }}
          TertiaryButtonLabel="Manage eligibility qualification"
          TertiaryButtonProps={{ onClick: showQualificationForm }}
        />
      ) : hasEducation ? (
        <StudentQualificationSelectOption
          PrimaryButtonLabel="Use my education details"
          PrimaryButtonProps={{ onClick: useEducationDetailForEligibility }}
          SecondaryButtonLabel="Create my eligibility qualification"
          SecondaryButtonProps={{ onClick: showQualificationForm }}
        />
      ) : hasQualification ? (
        <StudentQualificationSelectOption
          PrimaryButtonLabel="Use my eligibility qualification"
          PrimaryButtonProps={{ onClick: useQualificationForEligibility }}
          SecondaryButtonLabel="Create my education details"
          SecondaryButtonProps={{
            onClick: gotoEducationEditPage,
          }}
        />
      ) : (
        <StudentQualificationSelectOption
          PrimaryButtonLabel="Create my education details"
          PrimaryButtonProps={{
            onClick: gotoEducationEditPage,
          }}
          SecondaryButtonLabel="Create my eligibility qualification"
          SecondaryButtonProps={{ onClick: showQualificationForm }}
        />
      )}
    </Box>
  );
};
